import React, { useState, useRef, useEffect } from 'react';
import './FridgeScanner.css';

// X icon for close button
const closeIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23545F71' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='9' y1='9' x2='15' y2='15'%3E%3C/line%3E%3Cline x1='15' y1='9' x2='9' y2='15'%3E%3C/line%3E%3C/svg%3E";

// Camera icon
const cameraIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23007A33' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z'%3E%3C/path%3E%3Ccircle cx='12' cy='13' r='4'%3E%3C/circle%3E%3C/svg%3E";

// Gallery icon
const galleryIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23007A33' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'%3E%3C/circle%3E%3Cpolyline points='21 15 16 10 5 21'%3E%3C/polyline%3E%3C/svg%3E";

// Check icon for selected ingredients
const checkIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E";

// Plus icon for adding ingredients
const plusIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='12' y1='5' x2='12' y2='19'%3E%3C/line%3E%3Cline x1='5' y1='12' x2='19' y2='12'%3E%3C/line%3E%3C/svg%3E";

const SCANNER_STEPS = {
  INSTRUCTIONS: 'instructions',
  LIVE_CAMERA: 'live_camera',
  LOADING: 'loading',
  RESULTS: 'results'
};

const CAPTURE_MODES = {
  PHOTO: 'photo',
  VIDEO: 'video'
};

const MAX_VIDEO_DURATION = 30; // 30 seconds

const API_URL = process.env.REACT_APP_INGREDIENT_API_URL;
const API_KEY = process.env.REACT_APP_INGREDIENT_API_KEY;

const getImageRecognitionErrorMessage = (status) => {
  switch (status) {
    case 400:
      return "That file format and/or size isn't in our cookbook. Can you give it another go?";
    case 401:
      return 'Oops, we’re juggling a few too many plates right now. Could you give us another shot in a minute or two? Appreciate you hanging in there!';
    case 500:
      return null;
    case 422:
      return 'Uh oh, one or more of these ingredients is not part of our data base. Can you please check and try again?';
    default:
      return null;
  }
};

function FridgeScanner({ isOpen, onClose, onIngredientsDetected }) {
  const [step, setStep] = useState(SCANNER_STEPS.INSTRUCTIONS);
  const [captureMode, setCaptureMode] = useState(CAPTURE_MODES.PHOTO);
  const [detectedIngredients, setDetectedIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [error, setError] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  // Clean up camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [stream]);

  // Stop camera when modal closes
  useEffect(() => {
    if (!isOpen && stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [isOpen, stream]);

  const resetScanner = () => {
    // Stop camera if active
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    setStep(SCANNER_STEPS.INSTRUCTIONS);
    setCaptureMode(CAPTURE_MODES.PHOTO);
    setDetectedIngredients([]);
    setSelectedIngredients([]);
    setError(null);
    setCapturedImage(null);
    setIsRecording(false);
    setRecordingTime(0);
  };

  const handleClose = () => {
    resetScanner();
    onClose();
  };

  // Open camera automatically after instructions
  const handleContinueFromInstructions = async () => {
    await openLiveCamera();
  };

  // Open live camera
  const openLiveCamera = async () => {
    console.log('Opening camera...');
    try {
      const constraints = { 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      // Add audio only for video mode
      if (captureMode === CAPTURE_MODES.VIDEO) {
        constraints.audio = false;
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('Camera stream obtained:', mediaStream);
      setStream(mediaStream);
      setStep(SCANNER_STEPS.LIVE_CAMERA);
      setError(null);

      
      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          console.log('Video stream attached to video element');
        }
      }, 100);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Could not access camera. Please grant permission or use gallery.');
    }
  };

  // Switch between photo and video modes
  const switchCaptureMode = async (mode) => {
    if (isRecording) return; // Don't switch while recording
    
    setCaptureMode(mode);
    
    // Restart camera with/without audio based on mode
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    // Small delay before reopening
    setTimeout(async () => {
      await openLiveCamera();
    }, 100);
  };

  // Capture photo from live camera
  const capturePhotoFromCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    console.log('Capturing photo...', { video, canvas });
    
    if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw current video frame to canvas
      context.drawImage(video, 0, 0);
      
      console.log('Photo captured to canvas');
      
      // Stop camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      
      // Convert canvas to blob/file
      canvas.toBlob((blob) => {
        const file = new File([blob], `fridge-scan-${Date.now()}.jpg`, { 
          type: 'image/jpeg' 
        });
        
        console.log('File created:', file);
        
        // Process the captured image
        handleMediaCapture(file);
      }, 'image/jpeg', 0.95);
    } else {
      console.error('Video not ready:', video?.readyState);
      setError('Camera not ready. Please wait a moment and try again.');
    }
  };

  // Start video recording
  const startVideoRecording = () => {
    if (!stream) return;
    
    try {
      recordedChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/mp4'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/mp4' });
        const file = new File([blob], `fridge-scan-${Date.now()}.mp4`, { 
          type: 'video/mp4' 
        });
        
        // Stop camera stream
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }
        
        handleMediaCapture(file);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= MAX_VIDEO_DURATION) {
            stopVideoRecording();
          }
          return newTime;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording. Please try again.');
    }
  };

  // Stop video recording
  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Handle capture button click
  const handleCapture = () => {
    if (captureMode === CAPTURE_MODES.PHOTO) {
      capturePhotoFromCamera();
    } else {
      if (isRecording) {
        stopVideoRecording();
      } else {
        startVideoRecording();
      }
    }
  };

  // Close live camera
  const closeLiveCamera = () => {
    if (isRecording) {
      stopVideoRecording();
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    handleClose();
  };

  // Handle file upload from gallery (photo or video)
  const handleGalleryUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleMediaCapture(file);
    }
  };

  // Handle media capture (photo or video)
  const handleMediaCapture = async (file) => {
    if (!file) return;

    // Show loading state
    setStep(SCANNER_STEPS.LOADING);
    setError(null);

    // Create preview URL for the captured media
    const mediaUrl = URL.createObjectURL(file);
    setCapturedImage(mediaUrl);

    try {
      if (!API_URL || !API_KEY) {
        throw new Error('Missing ingredient API configuration. Please set REACT_APP_INGREDIENT_API_URL and REACT_APP_INGREDIENT_API_KEY in your .env file.');
      }

      // Create FormData for API request
      const formData = new FormData();
      formData.append('file', file);
      formData.append('brand', 'knorr');
      formData.append('locale', 'en-pk');
      formData.append('version', 'v2');

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY,
        },
        body: formData,
        mode: 'cors'
      });

      if (!response.ok) {
        const brandMessage = getImageRecognitionErrorMessage(response.status);
        throw new Error(brandMessage || 'Failed to process the media. Please try again.');
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.ingredients) {
        const ingredients = result.data.ingredients;
        setDetectedIngredients(ingredients);
        // Auto-select all detected ingredients
        setSelectedIngredients([...ingredients]);
        setStep(SCANNER_STEPS.RESULTS);
      } else {
        throw new Error('No ingredients detected in the media');
      }
    } catch (err) {
      // Re-open camera on error
      console.error('Ingredient detection error:', err);
      setError(err.message || 'Failed to detect ingredients. Please try again.');
      setStep(SCANNER_STEPS.LIVE_CAMERA);
      setTimeout(() => {
        openLiveCamera();
      }, 6000);
    }
  };

  const toggleIngredient = (ingredient) => {
    if (selectedIngredients.includes(ingredient)) {
      setSelectedIngredients(selectedIngredients.filter(i => i !== ingredient));
    } else {
      setSelectedIngredients([...selectedIngredients, ingredient]);
    }
  };

  const handleAddIngredients = () => {
    if (selectedIngredients.length > 0) {
      onIngredientsDetected(selectedIngredients);
      handleClose();
    }
  };

  const handleRetake = async () => {
    setDetectedIngredients([]);
    setSelectedIngredients([]);
    setCapturedImage(null);
    await openLiveCamera();
  };

  // Format recording time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fridge-scanner-overlay">
      <div className="fridge-scanner-modal">
        {/* Close Button */}
        <button className="scanner-close-btn" onClick={handleClose}>
          <img src={closeIcon} alt="Close" />
        </button>

        {/* Instructions Step */}
        {step === SCANNER_STEPS.INSTRUCTIONS && (
          <div className="scanner-instructions">
            <div className="instruction-card">
              <span className="instruction-number">1</span>
              <p className="instruction-text">
                Scan your pantry using the camera.
              </p>
            </div>
            <div className="instruction-card">
              <span className="instruction-number">2</span>
              <p className="instruction-text">
                Point at the ingredients you’d like to use.​
              </p>
            </div>
            <div className="instruction-card">
              <span className="instruction-number">3</span>
              <p className="instruction-text">
                Make sure your image or video is well lit.
              </p>
            </div>
            
            <button 
              className="scanner-continue-btn"
              onClick={handleContinueFromInstructions}
            >
              Continue
            </button>
          </div>
        )}

        {/* Live Camera Step */}
        {step === SCANNER_STEPS.LIVE_CAMERA && (
          <div className="scanner-live-camera">
           
            <div className='previewandmode'>
              <div className="live-camera-preview">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline
                    muted
                    className="camera-video"
                  />
                  
                  {/* Recording indicator and timer */}
                  {isRecording && (
                    <div className="recording-indicator">
                      <span className="recording-dot"></span>
                      <span className="recording-time">
                        {formatTime(recordingTime)} / {formatTime(MAX_VIDEO_DURATION)}
                      </span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="scanner-error">
                    <p>{error}</p>
                  </div>
                )}

                {/* Mode Switcher */}
                <div className="mode-switcher">
                  <button 
                    className={`mode-btn ${captureMode === CAPTURE_MODES.PHOTO ? 'active' : ''}`}
                    onClick={() => switchCaptureMode(CAPTURE_MODES.PHOTO)}
                    disabled={isRecording}
                  >
                    Photo
                  </button>
                  <button 
                    className={`mode-btn ${captureMode === CAPTURE_MODES.VIDEO ? 'active' : ''}`}
                    onClick={() => switchCaptureMode(CAPTURE_MODES.VIDEO)}
                    disabled={isRecording}
                  >
                    Video
                  </button>
                </div>

            </div>
            

            <div className="live-camera-actions">
              {/* Cancel Button */}
              <button 
                className="camera-icon-btn"
                // onClick={closeLiveCamera}
              >
               
              </button>
              
              {/* Capture/Record Button */}
              <button 
                className={`camera-capture-btn ${isRecording ? 'recording' : ''}`}
                onClick={handleCapture}
              >
                <div className="capture-btn-inner"></div>
              </button>
              
              {/* Gallery Button */}
              <div className="gallery-btn-wrapper">
                <button 
                  className="camera-icon-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isRecording}
                 
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <img src={galleryIcon} alt="Gallery" width="48" height="48" style={{ display: 'block', margin: '0 auto' }} />
                  </span>
                </button>
                {/* <div className="gallery-label" style={{textAlign: 'center', fontSize: '0.7em', marginTop: '1px', color: 'green'}}>Gallery</div> */}
              </div>
            </div>
            
            
            {/* Hidden file input for gallery */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*,video/*"
              onChange={handleGalleryUpload}
              style={{ display: 'none' }}
            />

            {/* Hidden canvas for photo capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        )}

        {/* Loading Step */}
        {step === SCANNER_STEPS.LOADING && (
          <div className="scanner-loading">
            <div className="loading-spinner"></div>
            <p className="loading-text">Detecting ingredients...</p>
            {capturedImage && (
              <img src={capturedImage} alt="Processing" className="loading-preview" />
            )}
          </div>
        )}

        {/* Results Step */}
        {step === SCANNER_STEPS.RESULTS && (
          <div className="scanner-results">
            <h3 className="results-title">Detected Ingredients</h3>
            <p className="results-subtitle">
              Select the ingredients you want to add
            </p>

            <div className="detected-ingredients-list">
              {detectedIngredients.map((ingredient, index) => (
                <button
                  key={index}
                  className={`detected-ingredient ${selectedIngredients.includes(ingredient) ? 'selected' : ''}`}
                  onClick={() => toggleIngredient(ingredient)}
                >
                  <span className="ingredient-name">{ingredient}</span>
                  <span className="ingredient-toggle">
                    {selectedIngredients.includes(ingredient) ? (
                      <img src={checkIcon} alt="Selected" />
                    ) : (
                      <img src={plusIcon} alt="Add" />
                    )}
                  </span>
                </button>
              ))}
            </div>

            <div className="results-actions">
              <button className="retake-btn" onClick={handleRetake}>
                Retake
              </button>
              <button 
                className="add-ingredients-btn"
                onClick={handleAddIngredients}
                disabled={selectedIngredients.length === 0}
              >
                Add {selectedIngredients.length} Ingredient{selectedIngredients.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FridgeScanner;